import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { useI18n } from '../../contexts/I18nContext';
import { FileText, Download, ArrowUpRight, ArrowDownRight, Search } from 'lucide-react';
import { Input } from '../../components/ui/input';
import api from '../../services/api';

const InvoicesPage = () => {
  const { t } = useI18n();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const res = await api.get('/wallet/invoices');
      setInvoices(res.data);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredInvoices = invoices.filter(inv =>
    inv.description?.toLowerCase().includes(search.toLowerCase()) ||
    inv.type?.toLowerCase().includes(search.toLowerCase())
  );

  const getTypeLabel = (type) => {
    const labels = {
      topup: 'Aufladung',
      debit: 'Abbuchung',
      file_upload: 'Datei-Upload',
      bonus: 'Bonus',
      admin_credit: 'Admin Gutschrift',
      admin_debit: 'Admin Abbuchung',
      refund: 'Erstattung',
      purchase: 'Shop-Kauf',
    };
    return labels[type] || type;
  };

  const getTypeColor = (type) => {
    if (['topup', 'bonus', 'admin_credit', 'refund'].includes(type)) return 'text-emerald-500';
    return 'text-red-400';
  };

  const isCredit = (type) => ['topup', 'bonus', 'admin_credit', 'refund'].includes(type);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="invoices-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-white">{t('nav.invoices')}</h1>
            <p className="text-sm text-slate-500 mt-1">{filteredInvoices.length} Transaktionen</p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              placeholder={t('common.search') + '...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field pl-9 h-9"
              data-testid="invoice-search"
            />
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1,2,3,4].map(i => (
              <div key={i} className="h-16 bg-white/5 animate-pulse rounded-xl" />
            ))}
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-white font-medium">Keine Transaktionen</p>
            <p className="text-sm text-slate-500 mt-1">Ihre Transaktionshistorie erscheint hier.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Header */}
            <div className="grid grid-cols-12 gap-4 px-4 py-2 text-xs text-slate-500 uppercase tracking-wider">
              <div className="col-span-1">Nr.</div>
              <div className="col-span-3">Datum</div>
              <div className="col-span-2">Typ</div>
              <div className="col-span-4">Beschreibung</div>
              <div className="col-span-2 text-right">Betrag</div>
            </div>

            {filteredInvoices.map((inv, idx) => (
              <div
                key={inv.id}
                className="grid grid-cols-12 gap-4 items-center px-4 py-3 rounded-xl bg-[#141418] border border-white/[0.04] hover:border-white/10 transition-colors"
                data-testid={`invoice-row-${idx}`}
              >
                <div className="col-span-1 text-xs text-slate-500 font-mono">
                  #{(filteredInvoices.length - idx).toString().padStart(3, '0')}
                </div>
                <div className="col-span-3 text-sm text-white/70">
                  {formatDate(inv.date)}
                </div>
                <div className="col-span-2">
                  <span className={`text-xs font-medium ${getTypeColor(inv.type)}`}>
                    {getTypeLabel(inv.type)}
                  </span>
                </div>
                <div className="col-span-4 text-sm text-white/50 truncate">
                  {inv.description}
                </div>
                <div className="col-span-2 text-right flex items-center justify-end gap-1">
                  {isCredit(inv.type) ? (
                    <ArrowUpRight className="w-3.5 h-3.5 text-emerald-500" />
                  ) : (
                    <ArrowDownRight className="w-3.5 h-3.5 text-red-400" />
                  )}
                  <span className={`text-sm font-semibold ${isCredit(inv.type) ? 'text-emerald-500' : 'text-red-400'}`}>
                    {isCredit(inv.type) ? '+' : '-'}{inv.amount} Credits
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default InvoicesPage;
